
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
    User,
    Upload,
    Save
} from "lucide-react";
import GeneralProfileSkeleton from "./skeletons/GeneralProfileSkeleton";
import { useGeneralProfileData } from "@/lib/profile/generalProfileData";

export default function GeneralProfileTab() {
    const {
        isDarkMode,
        isCurrentProfileLoading,
        isMemberLoading,
        register,
        handleSubmit,
        setValue,
        userImageWatch,
        errors,
        isDirty,
        isValid,
        onSubmit,
        fileToBase64,
    } = useGeneralProfileData();

    if (isMemberLoading || isCurrentProfileLoading) return <GeneralProfileSkeleton />;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
                <h3
                    className={`text-lg font-semibold mb-4 transition-colors duration-300 ${isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                >
                    Profile Information
                </h3>

                {/* Profile Photo */}
                <div className="mb-6">
                    <Label
                        className={`transition-colors duration-300 ${isDarkMode ? "text-gray-200" : "text-gray-900"
                            }`}
                    >
                        Profile Photo
                    </Label>
                    <div className="mt-2 flex items-center gap-4">
                        <div
                            className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-gray-700" : "bg-gray-100"
                                }`}
                        >
                            {userImageWatch ? (
                                <img
                                    src={userImageWatch}
                                    alt="Profile"
                                    className="w-20 h-20 rounded-full object-cover"
                                />
                            ) : (
                                <User
                                    className={`w-8 h-8 transition-colors duration-300 ${isDarkMode ? "text-gray-500" : "text-gray-400"
                                        }`}
                                />
                            )}
                        </div>
                        <div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    document.getElementById("profile-photo")?.click()
                                }
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Photo
                            </Button>
                            <input
                                id="profile-photo"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                    const base64 = await fileToBase64(file);
                                    setValue("UserImage", base64, { shouldDirty: true, shouldValidate: true });
                                    }
                                }}
                            />
                            <p
                                className={`text-xs mt-1 transition-colors duration-300 ${isDarkMode ? "text-gray-400" : "text-gray-500"
                                    }`}
                            >
                                JPG, PNG up to 5MB
                            </p>
                        </div>
                        {errors.UserImage && (
                            <p className="text-sm text-red-500 mt-2">
                                {String(errors.UserImage.message)}
                            </p>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                    <Label
                        htmlFor="firstName"
                        className={`transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                    >
                        First Name
                    </Label>
                    <Input
                        id="firstName"
                        {...register("FirstName", {
                        required: "First name is required",
                        minLength: {
                            value: 2,
                            message: "Must be at least 2 characters",
                        },
                        })}
                        className="mt-1"
                        placeholder="e.g. Jhon"
                    />
                    {errors.FirstName && (
                        <p className="text-sm text-red-500 mt-2">
                        {String(errors.FirstName.message)}
                        </p>
                    )}
                    </div>

                    <div>
                    <Label
                        htmlFor="lastName"
                        className={`transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                    >
                        Last Name
                    </Label>
                    <Input
                        id="lastName"
                        {...register("LastName", {
                        required: "Last name is required",
                        minLength: {
                            value: 2,
                            message: "Must be at least 2 characters",
                        },
                        })}
                        className="mt-1"
                        placeholder="e.g. Doe"
                    />
                    {errors.LastName && (
                        <p className="text-sm text-red-500 mt-2">
                        {String(errors.LastName.message)}
                        </p>
                    )}
                    </div>
                </div>

                    <div>
                        <Label
                            htmlFor="emailAddress"
                            className={`transition-colors duration-300 ${isDarkMode ? "text-gray-200" : "text-gray-900"
                                }`}
                        >
                            Email Address
                        </Label>
                        <Input
                            id="emailAddress"
                            type="email"
                            disabled
                            {...register("EmailAddress")}
                            className="mt-1"
                        />
                    </div>

                    <div>
                        <Label
                            htmlFor="gphcNumber"
                            className={`transition-colors duration-300 ${isDarkMode ? "text-gray-200" : "text-gray-900"
                                }`}
                        >
                            GPHC Number
                        </Label>
                        <Input
                            id="gphcNumber"
                            {...register("gphcNumber", {
                                required: "GPHC Number is required",
                                pattern: {
                                    value: /^[0-9]{6,8}$/,
                                    message: "Must be 6–8 digits",
                                },
                            })}
                            className="mt-1"
                            placeholder="e.g., GPhC: 1234567"
                        />
                        {errors.gphcNumber && (
                            <p className="text-sm text-red-500 mt-2">
                                {String(errors.gphcNumber.message)}
                            </p>
                        )}
                    </div>
                </div>
            </div>
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button
                    type="submit"
                    disabled={!isDirty || !isValid || isCurrentProfileLoading}
                    className={`bg-slate-900 hover:bg-slate-800 ${!isDirty || !isValid || isCurrentProfileLoading? "opacity-50 pointer-events-none" : ""
                        }`}
                >
                    <Save className="w-4 h-4 mr-2" />
                    {isCurrentProfileLoading ? "Saving..." : "Save Changes"}
                </Button>
            </div>
        </form>
    )
}
